import React, { useState, useEffect, useRef } from "react";
import { AxiosResponse } from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import { Utility } from "../../core/Utility";
import { cloneDeep } from "lodash";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { ProgressSpinner } from "primereact/progressspinner";
import { useTranslation } from "react-i18next";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ServicedMeasurement } from "../../models/ServicedMeasurement";
import { Warehouse } from "../../models/Warehouse";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllServicedMeasurements } from "../../services/NewVersionService";
import { useApplicationContext } from "../../application/ApplicationContext";
import "./ServicedMeasurementsPage.scss";

export const ServicedMeasurementsPage = () => {
  const { t } = useTranslation();

  const { state, selectedDateFromSet, selectedDateToSet } =
    useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear(); // Poslovna godina
  const yearStart = new Date(businessYear, 0, 1); // Početak godine
  const yearEnd = new Date(businessYear, 11, 31); // Kraj godine

  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      // Za tekuću godinu, postavi datume od 1. januara do danas
      const from = new Date(today.getFullYear(), 0, 1);
      const to = today;
      return { from, to };
    } else {
      // Za prethodne godine, postavi ceo opseg te godine
      return { from: yearStart, to: yearEnd };
    }
  };
  const [dateFrom, setDateFrom] = useState<Date>(
    () => getDefaultRangeForBusinessYear().from
  );
  const [dateTo, setDateTo] = useState<Date>(
    () => getDefaultRangeForBusinessYear().to
  );

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>(
    {} as Warehouse
  );
  const [servicedMeasurements, setServicedMeasurements] = useState<
    ServicedMeasurement[]
  >([]);
  const [filteredMeasurements, setFilteredMeasurements] = useState<
    ServicedMeasurement[]
  >([]);
  const [processing, setProcessing] = useState(false);
  const [filters, setFilters] = useState({
    measurementNumberFormatted: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    product: { value: null, matchMode: FilterMatchMode.CONTAINS },
    driverName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    vehicleNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const toast = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR")
      throw new Error(resp?.data?.message ?? "Greška");
  };

  const loadTableData = async () => {
    setProcessing(true);
    try {
      const [wResp, mResp]: [AxiosResponse, AxiosResponse] = await Promise.all([
        getAllWarehouses(),
        getAllServicedMeasurements(),
      ]);

      ensureSuccess(wResp);
      ensureSuccess(mResp);

      const whs = wResp?.data?.result ?? [];
      setWarehousesApi(whs);

      const allDocuments = mResp?.data?.result ?? [];
      setServicedMeasurements(allDocuments);
      setFilteredMeasurements(allDocuments);
      applyFilters(allDocuments, whs);
    } catch (error: any) {
      console.log(error);
      // @ts-ignore
      toast?.current?.show({
        severity: "error",
        summary: "Greška",
        detail: "Došlo je do greške pri učitavanju podataka",
        life: 3000,
      });
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    loadTableData();
  }, []);

  useEffect(() => {
    const refresh = (location.state as any)?.refresh === true;
    if (!refresh) return;

    loadTableData().finally(() => {
      navigate(location.pathname, { replace: true, state: null });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    selectedDateFromSet(from);
    selectedDateToSet(to);
  }, [businessYear]);

  useEffect(() => {
    applyFilters(servicedMeasurements);
  }, [selectedWarehouse, dateFrom, dateTo]);

  const applyFilters = (
    data: ServicedMeasurement[],
    whList: Warehouse[] = warehousesApi
  ) => {
    if (!data || data.length === 0) {
      setFilteredMeasurements([]);
      return;
    }
    let filteredData = data;
    if (selectedWarehouse?.id) {
      const warehouse = whList.find((item) => item.id === selectedWarehouse.id);
      filteredData = filteredData.filter(
        (m) => m.warehouse?.name === warehouse?.name
      );
    }
    const dateFromTime = dateFrom.getTime();
    const dateToTime = dateTo.getTime() + 86400000;
    filteredData = filteredData.filter((m) => {
      const docDate = new Date(m.date).getTime();
      return docDate >= dateFromTime && docDate <= dateToTime;
    });
    filteredData = [...filteredData].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    setFilteredMeasurements(filteredData);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewWarehouse = cloneDeep(selectedWarehouse);
    copyOfNewWarehouse = warehousesApi.find(
      (item) => item.id == value
    ) as Warehouse;
    setSelectedWarehouse(copyOfNewWarehouse);
  };

  const onDateFromChange = (_property: string, value: Date | null) => {
    const validDate = value && value >= yearStart ? value : yearStart;
    setDateFrom(validDate);
    selectedDateFromSet(validDate);
  };

  const onDateToChange = (_property: string, value: Date | null) => {
    const validDate = value && value <= yearEnd ? value : yearEnd;
    setDateTo(validDate);
    selectedDateToSet(validDate);
  };

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const onFilter = (e: any) => {
    setFilters(e.filters);
    const allFiltersEmpty = Object.values(e.filters).every(
      (filter: any) => filter.value === null || filter.value === ""
    );
    if (allFiltersEmpty) {
      applyFilters(servicedMeasurements);
    }
  };

  const resetFilters = () => {
    setSelectedWarehouse({} as Warehouse);
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    selectedDateFromSet(from);
    selectedDateToSet(to);
  };

  const actionBodyTemplate = (rowData: ServicedMeasurement) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => {
            navigate(`/serviced-measurement/${rowData.id}`);
          }}
        />
      </div>
    );
  };

  const dateBodyTemplate = (rowData: ServicedMeasurement) => {
    return rowData.date ? (
      <span>{Utility.formatDate(new Date(rowData.date))}</span>
    ) : (
      <></>
    );
  };

  const warehouseBodyTemplate = (rowData: ServicedMeasurement) => {
    return rowData.warehouse?.name.toUpperCase();
  };

  const partnerBodyTemplate = (rowData: ServicedMeasurement) => {
    return rowData.partner?.name;
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">{t("Uslužno merenje")}</span>
            <span className="header-elements-text">{`${t("u")}`}</span>
            <DropdownInput
              id="warehouse"
              value={selectedWarehouse?.id}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehousesApi.filter((item) => item.virtual !== true)}
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("do")}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={resetFilters}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 1rem",
              }}
              labelText={t("Novo merenje")}
              icon="bi bi-truck"
              onButtonClick={() => {
                navigate(`/serviced-measurement/${v4()}`, {
                  state: { mode: "create" },
                });
              }}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              value={filteredMeasurements}
              onFilter={onFilter}
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              emptyMessage="Nema unetih merenja"
              showGridlines
              stripedRows
            >
              <Column
                field="measurementNumberFormatted"
                filter
                sortable
                header={t("Br. merenja")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={dateBodyTemplate}
                header={t("Datum")}
                style={{ minWidth: "80px" }}
              />
              <Column
                body={warehouseBodyTemplate}
                header={t("Magacin")}
                style={{ minWidth: "90px" }}
              />
              <Column
                body={partnerBodyTemplate}
                header={t("Klijent")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="product"
                filter
                header={t("Naziv robe")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="driverName"
                filter
                header={t("Vozač")}
                style={{ minWidth: "80px" }}
              />
              <Column
                field="vehicleNumber"
                filter
                header={t("Broj registracije")}
                style={{ minWidth: "80px" }}
              />
              <Column
                field="brutto"
                header={t("Bruto")}
                style={{ minWidth: "70px" }}
              />
              <Column
                field="tara"
                header={t("Tara")}
                style={{ minWidth: "70px" }}
              />
              <Column
                field="netto"
                style={{ minWidth: "80px" }}
                header={t("Neto")}
              />
              <Column
                body={actionBodyTemplate}
                header={t("Opcije")}
                style={{ minWidth: "150px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
